import React, { useState } from "react";
import "./ScheduledReports.css";
import Calendar from "../../../components/Calendar/Calendar";

/* The mock array that used to live here is gone — the calendar and the day list
   are fed by props now, built from the scheduler in Home/homeSchedules.js.

   It was not merely unused: the component declared SCHEDULED_DATA and read it
   directly, ignoring the prop Home was already passing, so the section showed
   invented reports no matter what it was given. */

/* Every row is a report run, so they all take the same document glyph — the
   channel is what differs, and that is carried by the tile's colour. */
const ReportGlyph = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const ScheduledReportsOverview = ({ scheduledReportsData = [], loading = false, error = null, onViewAll }) => {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today);

  const getReportsForDate = (date) => {
    if (!date) return [];
    return scheduledReportsData.filter((r) => r.date && r.date.toDateString() === date.toDateString());
  };

  const formatSelectedDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  };

  const filteredReports = getReportsForDate(selectedDate);
  const reportDates = scheduledReportsData.map((r) => r.date).filter(Boolean);

  /* One run of one schedule, so the empty line has to distinguish "still
     loading" from "nothing due that day" — an unqualified "no reports" during
     the fetch reads as a definite answer the page does not have yet. */
  const emptyMessage = loading
    ? "Loading schedules…"
    : error
      ? "Could not load scheduled reports."
      : "No reports scheduled for this date";

  return (
    <div className="scheduled-reports-container">
      <div className="scheduled-reports-left-section">
        <Calendar selectedDate={selectedDate} onDateSelect={setSelectedDate} reportDates={reportDates} />
      </div>

      {/* The inner panel is taken out of the flow (see the CSS) so that the
          calendar alone decides how tall this row is. Without the extra
          element there is nothing to lift out, and the list's own length sets
          the height instead. */}
      <div className="scheduled-reports-right-section">
        <div className="scheduled-reports-panel">
          <div className="scheduled-reports-header">
            <div>
              <h3 className="scheduled-reports-title">Scheduled Reports</h3>
              <p className="scheduled-reports-subtitle">
                {selectedDate
                  ? `${formatSelectedDate(selectedDate)} · ${filteredReports.length} ${filteredReports.length === 1 ? "item" : "items"}`
                  : "No date selected"}
              </p>
            </div>
            {onViewAll ? (
              <button type="button" className="scheduled-reports-view-all" onClick={onViewAll}>
                View All
              </button>
            ) : null}
          </div>

          <div className="scheduled-reports-list">
            {filteredReports.length > 0 ? (
              filteredReports.map((report) => (
                <div key={report.id} className="scheduled-report-card">
                  <div
                    className="scheduled-report-icon"
                    style={{
                      backgroundColor: report.iconBg,
                      color: report.iconColor,
                    }}
                  >
                    {/* A schedule carries no icon of its own — the mock rows used
                      to supply one each. One document glyph for all of them, and
                      the channel shows through the tile's colour instead. */}
                    {report.icon || ReportGlyph}
                  </div>
                  <div className="scheduled-report-details">
                    <h5 className="scheduled-report-title">{report.title}</h5>
                    <p className="scheduled-report-description">{report.description}</p>
                    <div className="scheduled-report-meta">
                      <span className="scheduled-report-time">
                        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M7 3.5V7L9.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        {report.time}
                      </span>
                      {/* `duration` was a property of the mock rows; a schedule has
                        a file type instead. Each separator is tied to the value
                        after it, so a missing one does not leave a stray dot. */}
                      {report.fileType ? (
                        <>
                          <span className="scheduled-report-divider">·</span>
                          <span className="scheduled-report-duration">{report.fileType}</span>
                        </>
                      ) : null}
                      {report.type ? (
                        <>
                          <span className="scheduled-report-divider">·</span>
                          <span className="scheduled-report-type">{report.type}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="scheduled-reports-no-reports">
                <p>{emptyMessage}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduledReportsOverview;
